import { useState } from 'react';
import type { CSSProperties } from 'react';
import { WalletCards } from 'lucide-react';
import { ONE_NIGHT_CENTRE_CARDS } from '@ringmaster/shared';
import type { PublicView } from '@ringmaster/shared';
import { RoleGalleryOverlay } from './RoleGallery.js';
import { volumeIconBtn } from './VolumeControl.js';

/**
 * OneNightRolesButton — the persistent bottom-left corner control that answers
 * "what's in this game?" at any moment of a One Night game.
 *
 * WHY IT EXISTS: One Night's whole deduction hangs on the public card pool
 * (`n + 3` cards, three of them in the centre). That pool was only visible on
 * the DEAL screen, so by the time the table was arguing, nobody could check
 * what was in play, what a Joker even does, or when a swap lands. This button
 * carries the pool through the WHOLE game: a card icon, a count badge, and the
 * deck itself.
 *
 * It used to open a small anchored popover (a pool strip plus a text list). It
 * now opens {@link RoleGalleryOverlay} — the same full-screen deck the lobby
 * shows, with the REAL dealt pool passed in rather than a derived one, because
 * mid-game that pool is already public and authoritative. Everything the
 * popover taught moved with it: the "N roles dealt, M in the middle" title, the
 * per-role guide lines, the swap-order chips, the "You" mark on your own dealt
 * card, and the dim-when-absent rule.
 *
 * Reuse, not a fork: the button skin is `volumeIconBtn` — literally the same
 * 40px round corner control as the volume knob, so the corners of the app match.
 *
 * Two surfaces, one component (BackPill's idiom):
 *  - `position='fixed'` — the phone/hand surface (`/play`, Discord mobile, and
 *    the seated `/host`), lifted above the host's RingmasterBar via
 *    `offsetBottom` and stacked above FixedActionBar's gradient.
 *  - `position='static'` — the Discord desktop footer's left grid cell, where
 *    the layout already reserves the corner.
 *
 * Renders nothing outside a One Night room, and nothing before the pool exists
 * (LOBBY) — the button appears with the cards, at DEAL, and stays to END. The
 * lobby has its own "View roles" entry into the same screen.
 */
export function OneNightRolesButton({
  publicView,
  position = 'fixed',
  offsetBottom = 0,
}: {
  publicView: PublicView;
  position?: 'fixed' | 'static';
  /** Height of the fixed bottom chrome to clear (the host's RingmasterBar). */
  offsetBottom?: number;
}) {
  const [open, setOpen] = useState(false);
  const pool = publicView.oneNight?.pool;

  if (publicView.gameId !== 'onenight' || !pool || pool.length === 0) return null;

  const centreCount = publicView.oneNight?.centreCount ?? ONE_NIGHT_CENTRE_CARDS;
  const isFixed = position === 'fixed';
  // Deliberately reads NOTHING from the recipient's private view. The gallery
  // shows the public deck only, so this button needs no secret at all — the
  // safest possible version of a control that sits on screen all game.

  return (
    <div
      style={
        isFixed
          ? {
              position: 'fixed',
              left: 'max(16px, env(safe-area-inset-left, 0px))',
              bottom: offsetBottom
                ? `calc(${offsetBottom}px + 12px)`
                : 'max(16px, calc(env(safe-area-inset-bottom, 0px) + 8px))',
              // Above FixedActionBar (60) and RingmasterBar (60): both paint an
              // opaque full-width gradient across this corner.
              zIndex: 70,
            }
          : // Unpositioned — the mount point owns placement (BackPill's idiom).
            { position: 'relative' }
      }
    >
      <button
        type="button"
        className="rm-hover"
        style={{ ...volumeIconBtn, position: 'relative' }}
        onClick={() => setOpen(true)}
        // The badge is inside the button, so an aria-label would swallow the
        // count — say it instead.
        aria-label={`Roles in play (${pool.length})`}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <WalletCards size={16} aria-hidden />
        <span style={countBadge}>{pool.length}</span>
      </button>
      {open && (
        <RoleGalleryOverlay
          gameId="onenight"
          pool={pool}
          centreCount={centreCount}
          onClose={() => setOpen(false)}
        />
      )}
      {isFixed && (
        <style>{`
        /* Reserve the bottom-left corner so the centered action column and the
           caption plate never run under this button. Only phones need it — a
           viewport wider than the 360px action column plus both gutters already
           has the room. Scoped to this button's lifetime: it only ever mounts on
           a One Night hand surface. */
        @media (max-width: 520px) { :root { --rm-corner-inset: 56px; } }
      `}</style>
      )}
    </div>
  );
}

/**
 * Both moved to ui/RoleGallery.tsx with the screen that renders them (this
 * button's popover was replaced by it). Re-exported here so existing importers
 * — and the ui tests — don't have to care where the deck lives.
 */
export { orderChip, rolesDealtTitle } from './RoleGallery.js';

// ─── Styles ───────────────────────────────────────────────────────────────────

/** The count badge — RoleTally's badge geometry, pinned to the button's top corner. */
const countBadge: CSSProperties = {
  position: 'absolute',
  top: -6,
  right: -6,
  minWidth: 20,
  height: 20,
  padding: '0 5px',
  borderRadius: 999,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'var(--font-ui)',
  fontSize: 12,
  fontWeight: 700,
  color: '#fff',
  background: 'var(--gold-3, #a9762f)',
  border: '1px solid rgba(247,227,176,0.4)',
};
